import { useEffect, useMemo, useRef, useState } from 'react'
import { Screen } from '@/components/layout'
import { Banner, Button, SegmentedControl } from '@/components/primitives'
import { MetricRow, type MetricSpec } from '@/components/cards'
import { LineChart } from '@/components/charts/LineChart'
import { AmountStepper } from '@/components/pickers'
import { List, PayoffRow } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonCard, SkeletonMetrics } from '@/components/states'
import type { DebtPlan, Money } from '@/generated/types'
import { ApiFailure, api } from '@/lib/api'
import { t } from '@/lib/copy'
import { formatDate, formatMonthShort, formatMoney } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

type Strategy = DebtPlan['strategy']

const STRATEGIES: { value: Strategy; labelKey: 'debtPlanner.strategy.avalanche' | 'debtPlanner.strategy.snowball' | 'debtPlanner.strategy.minimum' }[] = [
  { value: 'avalanche', labelKey: 'debtPlanner.strategy.avalanche' },
  { value: 'snowball', labelKey: 'debtPlanner.strategy.snowball' },
  { value: 'minimum', labelKey: 'debtPlanner.strategy.minimum' },
]

/**
 * Switching strategy re-uses a cached result where one exists, so the three
 * options compare without a round trip once each has been computed.
 */
export function DebtPlannerScreen() {
  const navigate = useNavigate()
  const debt = useAsync(() => api.analysis.debt(), [])

  const [strategy, setStrategy] = useState<Strategy>('avalanche')
  const [payment, setPayment] = useState<Money | null>(null)
  const [computing, setComputing] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)
  const [saving, setSaving] = useState(false)

  // Cache key is payment + strategy: the same request never runs twice.
  const cache = useRef(new Map<string, DebtPlan>())
  const [plan, setPlan] = useState<DebtPlan | null>(null)

  const summary = debt.state.data
  const hasDebt = (summary?.totalOwed.amount ?? 0) > 0

  useEffect(() => {
    if (payment || !summary || !hasDebt) return
    const minimums = summary.accounts.reduce((sum, a) => sum + (a.minimumPayment?.amount ?? 0), 0)
    setPayment({
      amount: Math.max(minimums, Math.round(minimums * 1.5)),
      currency: summary.totalOwed.currency,
    })
  }, [payment, summary, hasDebt])

  useEffect(() => {
    if (!payment) return
    const key = `${strategy}:${payment.amount}`
    const cached = cache.current.get(key)
    if (cached) {
      setPlan(cached)
      return
    }
    let live = true
    setComputing(true)
    setError(null)
    api.analysis
      .plan(payment, strategy)
      .then((result) => {
        if (!live) return
        cache.current.set(key, result)
        setPlan(result)
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof ApiFailure ? e : null)
      })
      .finally(() => {
        if (live) setComputing(false)
      })
    return () => {
      live = false
    }
  }, [payment, strategy])

  const belowMinimum = useMemo(() => {
    if (!plan?.minimumPaymentTotal || !payment) return false
    return payment.amount < plan.minimumPaymentTotal.amount
  }, [plan, payment])

  const screenState =
    debt.state.status === 'error' || error
      ? 'error'
      : debt.state.status === 'loading' && !summary
        ? 'loading'
        : !hasDebt
          ? 'empty'
          : computing && !plan
            ? 'computing'
            : 'ready'

  const metrics: MetricSpec[] = plan
    ? [
        { key: 'free', label: t('debtPlanner.metric.debtFree'), value: formatDate(plan.payoffDate) },
        {
          key: 'interest',
          label: t('debtPlanner.metric.totalInterest'),
          value: formatMoney(plan.totalInterest, { absolute: true }),
        },
        {
          key: 'saved',
          label: t('debtPlanner.metric.saved'),
          value: formatMoney(plan.interestSavedVsMinimum, { absolute: true }),
          note:
            plan.monthsSavedVsMinimum !== undefined && plan.monthsSavedVsMinimum > 0
              ? t('debtPlanner.metric.monthsSaved', { count: plan.monthsSavedVsMinimum })
              : undefined,
          tone: 'positive',
        },
      ]
    : []

  const savePlan = async () => {
    if (!plan) return
    setSaving(true)
    try {
      await api.analysis.savePlan(plan)
      navigate('debt')
    } catch (e) {
      setError(e instanceof ApiFailure ? e : null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Screen id="debtPlanner" state={screenState} back={{ screen: 'debt' }}>
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard />
          <SkeletonMetrics count={3} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="debtPlanner.error.title"
          error={error ?? (debt.state.status === 'error' ? debt.state.error : undefined)}
          onRetry={() => {
            setError(null)
            debt.reload()
          }}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="debtPlanner.empty.title" />
      ) : (
        <div className="stack">
          <SegmentedControl
            options={STRATEGIES.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
            value={strategy}
            onChange={setStrategy}
            label={t('debtPlanner.title')}
          />

          {payment ? (
            <div className="card">
              <AmountStepper value={payment} onChange={setPayment} label={t('debtPlanner.payment.label')} />
            </div>
          ) : null}

          {belowMinimum && plan?.minimumPaymentTotal ? (
            <Banner tone="caution">
              {t('debtPlanner.belowMinimum', {
                amount: formatMoney(plan.minimumPaymentTotal, { absolute: true }),
              })}
            </Banner>
          ) : null}

          {plan ? (
            <>
              <MetricRow metrics={metrics} />

              {plan.schedule && plan.schedule.length > 1 ? (
                <div className="card card-pad-lg" aria-busy={computing}>
                  <LineChart
                    points={plan.schedule.map((entry) => ({
                      label: formatMonthShort(entry.month),
                      value: entry.remaining.amount,
                    }))}
                    caption={t('debtPlanner.title')}
                    fromZero
                    formatValue={(value) =>
                      formatMoney({ amount: value, currency: plan.totalOwed?.currency ?? 'USD' }, { absolute: true })
                    }
                  />
                </div>
              ) : null}

              <List>
                {(plan.debts ?? []).map((entry) => (
                  <PayoffRow key={entry.accountId} debt={entry} />
                ))}
              </List>

              <Button variant="primary" onClick={() => void savePlan()} disabled={saving || belowMinimum}>
                {t('debtPlanner.cta.save')}
              </Button>
            </>
          ) : (
            <SkeletonMetrics count={3} />
          )}
        </div>
      )}
    </Screen>
  )
}
